import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TaskTableEmptyState from "./TaskTableEmptyState";
import noTasksImage from "../../Images/Tasks/no-tasks.svg";
import TaskTableRowActionsMenu from "./TaskTableRowActionsMenu";
import {
  BULK_ACTIONS,
  TaskTableBulkActionsBar,
  buildTaskBulkActionOptions,
} from "./taskTableBulkActions";
import {
  DEFAULT_PRIORITY_OPTIONS,
  mergeStatusOptionsWithDefaults,
  DEFAULT_TYPE_OPTIONS,
  getFieldOptionChipSx,
  getFieldOptionValue,
  normalizeFieldOptions,
  taskTableInlineSelectSx,
  taskTableInlineSelectStatusSx,
} from "../../utils/taskTemplateChipStyles";
import { renderTaskDeadlineCell, renderTaskModifiedOnCell } from "../../utils/taskDeadlineDisplay";
import {
  TASK_TABLE_DEFAULT_ROWS_PER_PAGE,
  TASK_TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../utils/taskTablePagination";
import {
  taskTableContainerBorderedSx,
  taskTableGridTableSx,
  taskTableOuterBorder,
} from "../../utils/taskTableGridSx";
import {
  taskTableBodyScrollSx,
  taskTableBoundedPaperSx,
  taskTableFooterShrinkSx,
  taskTableNaturalPaperSx,
} from "../../utils/taskTableScrollSx";

/** List rows include `_accessFlags` from the server (omit for legacy callers). */
const canOpenTaskRow = (row) =>
  row._accessFlags == null ? true : row._accessFlags.canOpenDetail !== false;

const canMemberEditRow = (row) =>
  row._accessFlags == null
    ? true
    : !!(row._accessFlags.canFullEdit || row._accessFlags.canEditChecklists);

const canMemberDeleteRow = (row) => (row._accessFlags == null ? true : !!row._accessFlags.canDelete);

const canUpdateStatusRow = (row) =>
  row._accessFlags == null ? false : !!row._accessFlags.canUpdateStatus;

const canUpdateTypeRow = (row) =>
  row._accessFlags == null ? false : !!row._accessFlags.canUpdateType;

const resolveFieldOptionsList = (fieldOptions, kind) => {
  const key = kind === "status" ? "statusOptions" : kind === "priority" ? "priorityOptions" : "typeOptions";
  if (kind === "status") {
    return mergeStatusOptionsWithDefaults(fieldOptions?.statusOptions || []);
  }
  const defaults =
    kind === "priority" ? DEFAULT_PRIORITY_OPTIONS : DEFAULT_TYPE_OPTIONS;
  const raw = fieldOptions?.[key]?.length ? fieldOptions[key] : defaults;
  return normalizeFieldOptions(raw, kind);
};

const renderColoredOptionChip = (value, options, kind) => {
  if (!value) return "—";
  return <Chip size="small" label={value} sx={getFieldOptionChipSx(value, options, kind)} />;
};

const PERSON_AVATAR_PALETTE = [
  "#6366f1", "#ec4899", "#f59e0b", "#10b981",
  "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6",
  "#f97316", "#06b6d4", "#84cc16", "#e11d48",
];

const personAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PERSON_AVATAR_PALETTE[Math.abs(hash) % PERSON_AVATAR_PALETTE.length];
};

/** Person cell: colored initial avatar + name (Created By / Assignee). */
const renderPersonCell = (name) => {
  // Slice sends "—" as the "nobody assigned" placeholder — no avatar, just the dash.
  if (!name || name === "—") return "—";
  const initial = String(name).trim()[0]?.toLocaleUpperCase() || "?";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85, minWidth: 0 }}>
      <Avatar sx={{ width: 24, height: 24, bgcolor: personAvatarColor(name), fontSize: "0.6rem", fontWeight: 700, color: "#fff" }}>
        {initial}
      </Avatar>
      <Typography
        component="span"
        sx={{
          fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

const checkboxCellSx = {
  py: 0.25,
  pl: 2,
  pr: 0,
  width: 44,
  minWidth: 44,
  maxWidth: 44,
  boxSizing: "border-box",
  overflow: "hidden",
  "&.MuiTableCell-paddingCheckbox": {
    pl: 2,
    pr: 0,
    width: 44,
    minWidth: 44,
    maxWidth: 44,
  },
};

const rowMenuCellSx = {
  width: 28,
  pl: 0,
  pr: 0.5,
  textAlign: "left",
};

const nameHeaderCellSx = {
  width: { xs: "15%", md: "18%" },
  minWidth: { xs: 140, md: 180 },
  pl: 0.75,
};

const deadlineHeaderCellSx = {
  width: { xs: "11%", md: "12%" },
};

const assigneeHeaderCellSx = {
  width: { xs: "11%", md: "12%" },
};

const createdByHeaderCellSx = {
  width: { xs: "11%", md: "12%" },
};

const nameBodyCellBaseSx = {
  py: 1.3,
  pl: 0.75,
  fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" },
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.4,
};

const textBodyCellSx = {
  py: 1.3,
  fontSize: { xs: "0.88rem", sm: "0.95rem", md: "0.98rem" },
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  lineHeight: 1.4,
};

const TasksListTable = ({
  rows,
  fieldOptions,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onStatusChange,
  onPriorityChange,
  onTypeChange,
  canView = true,
  canEdit = true,
  hidePagination = false,
  fillViewport = true,
  bordered = false,
  showBulkActionsBar = false,
  bulkBarInset = false,
  /** Teams overview etc.: grow with pagination; scroll on main page container */
  usePageScroll = false,
  /** Server-side pagination state — when provided, `rows` is already the current page. */
  pagination = { page: 1, limit: TASK_TABLE_DEFAULT_ROWS_PER_PAGE, total: 0 },
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState("");

  const useBulkBar = showBulkActionsBar || bordered;
  /** Stretch + internal scroll only for grouped member sections (hidePagination). */
  const stretchTableContainer = fillViewport && hidePagination;
  /** Paginated main list: natural height; main page scrolls (no inner table scrollbar). */
  const naturalTableHeight =
    usePageScroll || (bordered && !hidePagination && !stretchTableContainer);
  const boundedScroll = stretchTableContainer;

  const typeOptions = useMemo(() => resolveFieldOptionsList(fieldOptions, "type"), [fieldOptions]);
  const statusOptions = useMemo(() => resolveFieldOptionsList(fieldOptions, "status"), [fieldOptions]);

  // `rows` already arrives as exactly the rows to display — either the current server-side
  // page (main list) or the full grouped-member bucket (hidePagination).
  const displayRows = rows;

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)),
    [rows, selectedIds]
  );

  const hasSelection = selectedRows.length > 0;

  const deletableSelectedRows = useMemo(
    () => selectedRows.filter((row) => canEdit && canMemberDeleteRow(row)),
    [selectedRows, canEdit]
  );

  const bulkActionOptions = useMemo(
    () =>
      buildTaskBulkActionOptions(selectedRows, {
        canView,
        canEdit,
        canOpenTaskRow,
        canMemberEditRow,
        canMemberDeleteRow,
      }),
    [selectedRows, canView, canEdit]
  );

  useEffect(() => {
    if (!hasSelection) {
      setBulkAction("");
      return;
    }
    if (bulkAction && !bulkActionOptions.some((opt) => opt.value === bulkAction)) {
      setBulkAction("");
    }
  }, [bulkAction, bulkActionOptions, hasSelection]);

  const canApplyBulkAction = hasSelection && bulkAction && bulkActionOptions.some((o) => o.value === bulkAction);

  const allVisibleSelected =
    displayRows.length > 0 && displayRows.every((row) => selectedIds.includes(row.id));

  const someVisibleSelected =
    displayRows.some((row) => selectedIds.includes(row.id)) && !allVisibleSelected;

  const handleSelectAll = (checked) => {
    const visibleIds = displayRows.map((row) => row.id);
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleRowSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleChangePage = (_, newPage0) => onPageChange?.(newPage0 + 1);

  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange?.(parseInt(event.target.value, 10));
  };

  const handleApplyBulkAction = () => {
    if (!hasSelection || !bulkAction) return;

    if (bulkAction === BULK_ACTIONS.VIEW && selectedRows.length === 1) {
      onView?.(selectedRows[0]);
      setBulkAction("");
      return;
    }
    if (bulkAction === BULK_ACTIONS.EDIT && selectedRows.length === 1) {
      onEdit?.(selectedRows[0]);
      setBulkAction("");
      return;
    }
    if (bulkAction === BULK_ACTIONS.DELETE && deletableSelectedRows.length > 0) {
      if (onBulkDelete) {
        onBulkDelete(deletableSelectedRows);
      } else {
        deletableSelectedRows.forEach((row) => onDelete?.(row));
      }
      setSelectedIds((prev) => prev.filter((id) => !deletableSelectedRows.some((r) => r.id === id)));
      setBulkAction("");
    }
  };

  const tableColSpan = 9;

  // Row hamburger menu items (left of the task name). Extend this array to add
  // future per-row actions — the menu renders whatever is passed in.
  const buildRowActions = (row) => [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIcon sx={{ fontSize: "1.05rem" }} />,
      show: canEdit && canMemberEditRow(row),
      onClick: (r) => onEdit?.(r),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIcon sx={{ fontSize: "1.05rem" }} />,
      color: "error.main",
      show: canEdit && canMemberDeleteRow(row),
      onClick: (r) => onDelete?.(r),
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        ...(boundedScroll
          ? taskTableBoundedPaperSx
          : naturalTableHeight
            ? taskTableNaturalPaperSx
            : { flexShrink: 0 }),
        display: "flex",
        flexDirection: "column",
        backgroundColor: (theme) => theme.palette.background.paper,
        overflow: "hidden",
        boxSizing: "border-box",
        borderRadius: 2,
        border: (theme) => taskTableOuterBorder(theme),
      }}
    >
      <TableContainer
        sx={{
          ...(boundedScroll ? taskTableBodyScrollSx : undefined),
          ...(bordered && taskTableContainerBorderedSx),
        }}
      >
        <Table
          stickyHeader
          aria-label="tasks table"
          sx={{ ...taskTableGridTableSx(bordered), tableLayout: "fixed" }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={checkboxCellSx}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  sx={{ p: 0.2, m: 0 }}
                />
              </TableCell>
              <TableCell sx={rowMenuCellSx} aria-label="Actions" />
              <TableCell sx={nameHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Name</Typography>
              </TableCell>
              <TableCell sx={deadlineHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Deadline</Typography>
              </TableCell>
              <TableCell sx={createdByHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Created By</Typography>
              </TableCell>
              <TableCell sx={assigneeHeaderCellSx}>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Assignee</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Status</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Type</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={700} sx={{ fontSize: { xs: "0.9rem", sm: "0.98rem", md: "1rem" } }}>Modified On</Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell padding="checkbox" sx={checkboxCellSx}>
                    <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} />
                  </TableCell>
                  <TableCell sx={rowMenuCellSx} />
                  <TableCell sx={nameBodyCellBaseSx}>
                    <Skeleton width="70%" />
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    <Skeleton width="60%" />
                  </TableCell>
                  <TableCell sx={{ ...textBodyCellSx, ...createdByHeaderCellSx }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton width="55%" />
                    </Box>
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.85 }}>
                      <Skeleton variant="circular" width={24} height={24} />
                      <Skeleton width="55%" />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }}>
                    <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 999 }} />
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }}>
                    <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 999 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width="65%" />
                  </TableCell>
                </TableRow>
              ))
            ) : displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} align="center">
                  <TaskTableEmptyState
                    image={noTasksImage}
                    icon={AssignmentOutlinedIcon}
                    color="#0E9046"
                    title="No tasks found"
                    subtitle="Tasks you create or get assigned will show up here."
                  />
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox" sx={checkboxCellSx}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => handleRowSelect(row.id)}
                      sx={{ p: 0.2, m: 0 }}
                    />
                  </TableCell>
                  <TableCell sx={rowMenuCellSx} onClick={(e) => e.stopPropagation()}>
                    <TaskTableRowActionsMenu row={row} actions={buildRowActions(row)} />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...nameBodyCellBaseSx,
                      color: "text.primary",
                      cursor: canView && canOpenTaskRow(row) ? "pointer" : "default",
                      opacity: canView && canOpenTaskRow(row) ? 1 : 0.75,
                      ...(canView && canOpenTaskRow(row)
                        ? {
                            "& .task-name-text": {
                              transition: "color 0.15s ease",
                            },
                            "&:hover .task-name-text": {
                              color: "primary.main",
                              textDecoration: "underline",
                              textUnderlineOffset: "3px",
                              textDecorationThickness: "1px",
                            },
                          }
                        : {}),
                    }}
                    onClick={() => canView && canOpenTaskRow(row) && onView?.(row)}
                  >
                    <Box component="span" className="task-name-text">
                      {row.name}
                    </Box>
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {renderTaskDeadlineCell(row)}
                  </TableCell>
                  <TableCell sx={{ ...textBodyCellSx, ...createdByHeaderCellSx }}>
                    {renderPersonCell(row.createdBy)}
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {renderPersonCell(row.assigne)}
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }} onClick={(e) => e.stopPropagation()}>
                    {canUpdateStatusRow(row) ? (
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={row.status || ""}
                        onChange={(e) => onStatusChange?.(row, e.target.value)}
                        displayEmpty
                        renderValue={(selected) => renderColoredOptionChip(selected, statusOptions, "status")}
                        sx={taskTableInlineSelectStatusSx}
                      >
                        <MenuItem value="">—</MenuItem>
                        {statusOptions.map((opt) => {
                          const val = getFieldOptionValue(opt);
                          return (
                            <MenuItem key={val} value={val}>
                              {renderColoredOptionChip(val, statusOptions, "status")}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : row.status ? (
                      <Chip
                        size="small"
                        label={row.status}
                        sx={getFieldOptionChipSx(row.status, statusOptions, "status")}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1.3 }} onClick={(e) => e.stopPropagation()}>
                    {canUpdateTypeRow(row) ? (
                      <Select
                        variant="standard"
                        disableUnderline
                        size="small"
                        value={row.templateType || ""}
                        onChange={(e) => onTypeChange?.(row, e.target.value)}
                        displayEmpty
                        renderValue={(selected) => renderColoredOptionChip(selected, typeOptions, "type")}
                        sx={taskTableInlineSelectSx}
                      >
                        <MenuItem value="">—</MenuItem>
                        {typeOptions.map((opt) => {
                          const val = getFieldOptionValue(opt);
                          return (
                            <MenuItem key={val} value={val}>
                              {renderColoredOptionChip(val, typeOptions, "type")}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    ) : row.templateType ? (
                      <Chip
                        size="small"
                        label={row.templateType}
                        sx={getFieldOptionChipSx(row.templateType, typeOptions, "type")}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={textBodyCellSx}>
                    {renderTaskModifiedOnCell(row)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(useBulkBar || !hidePagination) &&
        (useBulkBar ? (
          <Box sx={boundedScroll || naturalTableHeight ? taskTableFooterShrinkSx : undefined}>
          <TaskTableBulkActionsBar
            totalCount={rows.length}
            selectedCount={selectedIds.length}
            bulkAction={bulkAction}
            onBulkActionChange={setBulkAction}
            bulkActionOptions={bulkActionOptions}
            onApply={handleApplyBulkAction}
            canApply={canApplyBulkAction}
            hasSelection={hasSelection}
            bordered={bordered}
            bulkBarInset={bulkBarInset}
            borderTop={(theme) => taskTableOuterBorder(theme)}
            pagination={
              hidePagination
                ? null
                : {
                    count: pagination.total,
                    page: pagination.page - 1,
                    onPageChange: handleChangePage,
                    rowsPerPage: pagination.limit,
                    onRowsPerPageChange: handleChangeRowsPerPage,
                    rowsPerPageOptions: TASK_TABLE_ROWS_PER_PAGE_OPTIONS,
                    sx: {
                      "& .MuiTablePagination-toolbar": {
                        minHeight: { xs: 40, sm: 44 },
                        px: 0,
                        flexWrap: "nowrap",
                      },
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                        fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
                      },
                      "& .MuiTablePagination-select": {
                        fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
                      },
                      "& .MuiIconButton-root": { p: 0.4 },
                    },
                  }
            }
          />
          </Box>
        ) : (
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={TASK_TABLE_ROWS_PER_PAGE_OPTIONS}
            sx={{
              ...(boundedScroll ? taskTableFooterShrinkSx : {}),
              width: "100%",
              m: 0,
              boxSizing: "border-box",
              ...(bordered && { borderTop: (theme) => taskTableOuterBorder(theme) }),
              "& .MuiTablePagination-toolbar": {
                minHeight: { xs: 44, sm: 48, md: 52 },
                px: bordered ? 1.5 : undefined,
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
              },
              "& .MuiTablePagination-select": {
                fontSize: { xs: "0.84rem", sm: "0.92rem", md: "0.96rem" },
              },
              "& .MuiIconButton-root": { p: 0.4 },
            }}
          />
        ))}
    </Paper>
  );
};

export default TasksListTable;
